import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@lean-agility/design-system/react';

const iconContainerVariants = cva('flex items-center justify-center rounded-lg', {
  variants: {
    size: {
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-16',
    },
    variant: {
      accent: `
        bg-action-primary/10
        dark:bg-action-primary/25
      `,
      muted: 'bg-bg-muted',
      error: `
        bg-feedback-error/10
        dark:bg-feedback-error/20
      `,
      success: `
        bg-feedback-success/10
        dark:bg-feedback-success/20
      `,
      info: `
        bg-feedback-info/10
        dark:bg-feedback-info/20
      `,
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'accent',
  },
});

export interface IconContainerProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof iconContainerVariants> {
  children: ReactNode;
}

/**
 * IconContainer - A container component for displaying icons with consistent styling.
 *
 * Provides a standardized way to display icons with background containers,
 * supporting different sizes and variants. Use the `className` prop to customize
 * the shape (e.g., `rounded-full` for circles).
 *
 * @example
 * ```tsx
 * <IconContainer size="md" variant="accent">
 *   <Rocket className="h-5 w-5 text-action-primary" />
 * </IconContainer>
 *
 * <IconContainer size="xl" variant="muted" className="rounded-full">
 *   <Database className="h-8 w-8 text-text-subtle" />
 * </IconContainer>
 * ```
 */
export function IconContainer({ className, size, variant, children, ...props }: IconContainerProps) {
  return (
    <div className={cn(iconContainerVariants({ size, variant }), className)} {...props}>
      {children}
    </div>
  );
}
